import QuoteStatusSelection from 'src/components/common/QuoteStatusSelection'
import {
  Alert,
  Grid,
  makeStyles,
  InputField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Spinner,
  IconButton
} from '@paintscout/ui'
import { useQuoteQuery } from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'
import { JobAddressDialog } from '@paintscout/react-quote'
import type { DialogProps } from '@paintscout/ui'
import { SingleDatePicker, DialogStackContext, useClientOptions, useUser } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import CloseIcon from '@material-ui/icons/Close'
import {
  calculateQuote,
  copyQuote,
  formatJobAddress,
  getJobAddress,
  getJobIdentifier,
  getObjectLabels,
  setDate,
  setJobAddress,
  setJobIdentifier,
  setStatus
} from '@paintscout/util/builder'
import { useQuoteMutations } from 'shared/hooks'
import history from 'shared/util/routing/history'
import type { Moment } from 'moment'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import type { QuoteStatus } from 'paintscout'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useConnection } from 'react-detect-offline'
import { getQuoteCopyFailureMessage, getQuoteLoadError } from './copyQuoteMessages'

const useStyles = makeStyles((_theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

export interface CopyQuoteDialogProps extends DialogProps {
  quoteId: string
  calculate?: boolean
  isInvoice?: boolean
}

function CopyQuoteDialog({
  quoteId,
  calculate,
  isInvoice,
  loading: propLoading,
  ...dialogProps
}: CopyQuoteDialogProps) {
  const classes = useStyles()
  const { online } = useConnection()
  const { dismissDialog, openDialog } = useContext(DialogStackContext)
  const { enqueueSnackbar } = useSnackbar()
  const { saveQuote } = useQuoteMutations()
  const { user } = useUser()

  // get options and object labels
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice: isInvoice })
  // const dateFormat = options.options.dateFormat.momentValue

  // fetch quote
  const { loading, data, error, refetch } = useQuoteQuery({
    // Note: this fetchPolicy is not the last word. `@wora/apollo-offline`'s ApolloClientOffline
    // rewrites it to 'cache-only' whenever its own detector says offline, then restores the label
    // on the observable — so neither branch guarantees a network request (#22824).
    fetchPolicy: online ? 'network-only' : 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !quoteId,
    variables: { id: quoteId },
    onError: useCallback((error) => {
      Sentry.captureException(error, { tags: { quoteId } })
      dismissDialog()
      enqueueSnackbar(`Unable to get ${objectLabels.quote.value} for duplicating`, { variant: 'error' })
    }, [])
  })

  const quote = data?.quote
  // form data
  const [formValues, setFormValues] = useState({
    jobAddress: null,
    jobIdentifier: null,
    status: 'draft',
    date: moment()
  })
  const [copying, setCopying] = useState(false)

  useEffect(() => {
    if (quote) {
      setFormValues({
        date: moment(quote.quote_date),
        jobAddress: getJobAddress({ quote }),
        jobIdentifier: getJobIdentifier({ quote }),
        status: 'draft'
      })
    }
  }, [quote])

  // `skip: !quoteId` stops the query erroring on a missing id, so onError no longer reports it.
  // Callers such as QuoteNextActionsMenu pass `quote?._id`, so a missing id is a real upstream bug
  // and must stay visible in Sentry.
  useEffect(() => {
    if (!quoteId) {
      Sentry.captureMessage('CopyQuoteDialog opened without a quoteId', { level: 'warning' })
    }
  }, [quoteId])

  const dialogLoading = loading || propLoading || copying

  // ApolloClientOffline keeps an online flag that is separate from useConnection()/ps-ping. When it
  // is false every query is downgraded to 'cache-only', so a cache miss returns no data and no
  // error. Feature-detected, so a plain ApolloClient is unaffected.
  const apolloClient = useApolloClient() as unknown as { isOnline?: () => boolean }
  const clientOffline = typeof apolloClient?.isOnline === 'function' && !apolloClient.isOnline()

  const loadError = getQuoteLoadError({
    quoteId,
    loading: dialogLoading,
    quote,
    error,
    clientOffline,
    quoteLabel: objectLabels.quote.value
  })

  return (
    <Dialog {...dialogProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        loading={dialogLoading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={dismissDialog} disabled={dialogLoading}>
            <CloseIcon />
          </IconButton>
        }
      >
        Duplicate {objectLabels.quote.value}
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        {loading ? (
          <Spinner />
        ) : loadError ? (
          <Alert severity="error" content={loadError} noMargin={true} />
        ) : (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12}>
              <QuoteStatusSelection
                name="status"
                label="Status"
                invoice={quote?.is_invoice}
                fullWidth={true}
                variant="single"
                margin="dense"
                value={formValues.status as QuoteStatus}
                onChange={(newStatus: DropdownSelectOption) =>
                  setFormValues((values) => ({ ...values, status: newStatus.value }))
                }
                popperModifiers={{
                  flip: {
                    enabled: false
                  },
                  preventOverflow: {
                    enabled: false
                  }
                }}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <SingleDatePicker
                label="Date"
                date={formValues.date}
                onChange={(newDate: Moment) => setFormValues((values) => ({ ...values, date: newDate }))}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <InputField
                label="Job Address"
                value={
                  formatJobAddress({ jobAddress: formValues.jobAddress, jobIdentifier: formValues.jobIdentifier }) || ''
                }
                margin="dense"
                placeholder="No Job Address"
                fullWidth={true}
                multiline={true}
                onClick={() => {
                  openDialog(JobAddressDialog, {
                    jobAddress: formValues.jobAddress,
                    jobIdentifier: formValues.jobIdentifier,
                    // assign job address
                    onConfirm: ({
                      identifier,
                      ...address
                    }: {
                      address: string
                      province: string
                      city: string
                      postal: string
                      identifier: string
                    }) => {
                      setFormValues((values) => ({ ...values, jobAddress: address, jobIdentifier: identifier }))
                      dismissDialog()
                    },
                    onCancel: dismissDialog
                  })
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          loadError && refetch ? (
            <Button
              variant="text"
              disabled={dialogLoading}
              onClick={() => {
                // A rejected refetch is its own code path — the query's onError does not fire for
                // it — so report it and tell the user, rather than failing silently.
                refetch().catch((refetchError) => {
                  Sentry.captureException(refetchError, { tags: { quoteId }, extra: { clientOffline } })
                  enqueueSnackbar(
                    `Still unable to load ${objectLabels.quote.value}. Reload the page, then try again.`,
                    {
                      variant: 'error'
                    }
                  )
                })
              }}
            >
              Try again
            </Button>
          ) : undefined
        }
      >
        <Button
          loading={copying}
          disabled={dialogLoading || !!loadError}
          onClick={async () => {
            setCopying(true)
            try {
              let copiedQuote = copyQuote({ quote, user, options })

              if (formValues.jobAddress) {
                copiedQuote = setJobAddress({ quote: copiedQuote, jobAddress: formValues.jobAddress })
              }

              if (formValues.jobIdentifier) {
                copiedQuote = setJobIdentifier({ quote: copiedQuote, identifier: formValues.jobIdentifier })
              }

              copiedQuote = setDate({ quote: copiedQuote, date: formValues.date.unix() * 1000 })

              if (formValues.status) {
                copiedQuote = setStatus({ quote: copiedQuote, status: formValues.status as QuoteStatus })
              }

              if (calculate) {
                copiedQuote = calculateQuote({ quote: copiedQuote, options })
              }

              await saveQuote({ quote: copiedQuote })

              history.push(`/${copiedQuote.is_invoice ? 'invoices' : 'quotes'}/edit/${copiedQuote._id}`)
              enqueueSnackbar(`${objectLabels.quote.value} duplicated`, { variant: 'success' })

              dismissDialog()
            } catch (error) {
              Sentry.captureException(error, {
                tags: { quoteId },
                extra: { clientOffline, hasQuote: !!quote, queryError: (error as Error)?.message }
              })
              setCopying(false)
              enqueueSnackbar(getQuoteCopyFailureMessage({ error, quoteLabel: objectLabels.quote.value }), {
                variant: 'error'
              })
            }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CopyQuoteDialog
